import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSquads,
  createSquad,
  getSquadMembers,
  addSquadMember,
  removeSquadMember,
  deleteSquad,
  createSession,
} from '../../services/api';
import { shouldUseMobileUI } from '../../utils/deviceDetection';
import MobileNav from '../Mobile/MobileNav';
import './SquadManager.css';

function SquadManager({ user }) {
  const navigate = useNavigate();
  const [squads, setSquads] = useState([]);
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newSquadName, setNewSquadName] = useState('');
  const [newSquadDescription, setNewSquadDescription] = useState('');
  const [newMemberUsername, setNewMemberUsername] = useState('');
  const [error, setError] = useState('');
  
  // Mobile UI state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile] = useState(shouldUseMobileUI());

  useEffect(() => {
    loadSquads();
  }, []);

  useEffect(() => {
    if (selectedSquad) {
      loadMembers(selectedSquad.id);
    }
  }, [selectedSquad]);

  const loadSquads = async () => {
    try {
      const data = await getSquads();
      setSquads(data.squads || []);
      if (data.squads && data.squads.length > 0) {
        setSelectedSquad(data.squads[0]);
      }
    } catch (error) {
      console.error('Failed to load squads:', error);
      setError('Failed to load squads');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (squadId) => {
    try {
      const data = await getSquadMembers(squadId);
      setMembers(data.members || []);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const handleCreateSquad = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await createSquad(newSquadName, newSquadDescription);
      setShowCreateModal(false);
      setNewSquadName('');
      setNewSquadDescription('');
      await loadSquads();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create squad');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await addSquadMember(selectedSquad.id, newMemberUsername);
      setShowAddMemberModal(false);
      setNewMemberUsername('');
      await loadMembers(selectedSquad.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await removeSquadMember(selectedSquad.id, userId);
      await loadMembers(selectedSquad.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleDeleteSquad = async () => {
    if (!selectedSquad) return;

    const confirmMessage = `Are you sure you want to delete "${selectedSquad.name}"?\n\nAll members will lose access, but historic data will be preserved.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteSquad(selectedSquad.id);
      setSelectedSquad(null);
      await loadSquads();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete squad');
    }
  };

  const handleStartSession = async () => {
    if (!selectedSquad) return;

    try {
      const session = await createSession(selectedSquad.id, `${selectedSquad.name} Session`);
      navigate(`/session/${session.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start session');
    }
  };

  if (loading) {
    return <div className="squad-manager loading">Loading squads...</div>;
  }

  return (
    <div className="squad-manager">
      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          user={user}
          onLogout={() => navigate('/')}
        />
      )}

      <header className="squad-header">
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          ← Back to Dashboard
        </button>
        <h1>Squad Manager</h1>
        {isMobile ? (
          <button 
            className="btn-hamburger" 
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        ) : (
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Create Squad
          </button>
        )}
      </header>

      <div className="squad-content">
        <aside className="squads-sidebar">
          <h3>Your Squads</h3>
          {squads.length === 0 ? (
            <div className="empty-state">
              <p>No squads yet</p>
            </div>
          ) : (
            <div className="squads-list">
              {squads.map((squad) => (
                <div
                  key={squad.id}
                  className={`squad-item ${selectedSquad?.id === squad.id ? 'active' : ''}`}
                  onClick={() => setSelectedSquad(squad)}
                >
                  <h4>{squad.name}</h4>
                  <p>{squad.memberCount} members</p>
                </div>
              ))}
            </div>
          )}
        </aside>

        <main className="squad-main">
          {selectedSquad ? (
            <>
              <div className="squad-details card">
                <div className="squad-details-header">
                  <div>
                    <h2>{selectedSquad.name}</h2>
                    <p className="text-muted">{selectedSquad.description || 'No description'}</p>
                    <span className="badge">{selectedSquad.role}</span>
                  </div>
                  <div className="squad-actions">
                    <button className="btn btn-primary" onClick={handleStartSession}>
                      Start Session
                    </button>
                    {selectedSquad.role === 'owner' && (
                      <button className="btn btn-danger" onClick={handleDeleteSquad}>
                        🗑️ Delete Squad
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="members-section card">
                <div className="members-header">
                  <h3>Members ({members.length})</h3>
                  {(selectedSquad.role === 'owner' || selectedSquad.role === 'admin') && (
                    <button className="btn btn-primary" onClick={() => setShowAddMemberModal(true)}>
                      Add Member
                    </button>
                  )}
                </div>

                <div className="members-list">
                  {members.map((member) => (
                    <div key={member.id} className="member-item">
                      <div className="member-info">
                        <strong>{member.displayName || member.username}</strong>
                        <span className="text-muted">@{member.username}</span>
                        <span className="badge">{member.role}</span>
                      </div>
                      {(selectedSquad.role === 'owner' || selectedSquad.role === 'admin') &&
                        member.role !== 'owner' &&
                        member.userId !== user.id && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveMember(member.userId)}
                          >
                            Remove
                          </button>
                        )}
                    </div>
                  ))}
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
            </>
          ) : (
            <div className="empty-state">
              <p>Select a squad or create a new one</p>
            </div>
          )}
        </main>
      </div>

      {/* Create Squad Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Squad</h3>
            <form onSubmit={handleCreateSquad}>
              <div className="form-group">
                <label>Squad Name</label>
                <input
                  type="text"
                  className="input"
                  value={newSquadName}
                  onChange={(e) => setNewSquadName(e.target.value)}
                  required
                  minLength={3}
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  className="input"
                  value={newSquadDescription}
                  onChange={(e) => setNewSquadDescription(e.target.value)}
                  rows={3}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Squad Member</h3>
            <form onSubmit={handleAddMember}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  className="input"
                  value={newMemberUsername}
                  onChange={(e) => setNewMemberUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddMemberModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SquadManager;

